import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ApplicationStatusDropdown } from "./ApplicationStatusDropdown";
import { CheckCircle2, XCircle, AlertTriangle, Target, Lightbulb, ArrowRight, FileText, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface QualificationMatchResultsProps {
  result: any;
  reportVersion: string;
  savedJobId?: string;
  jobTitle?: string;
  companyName?: string;
  jobDescriptionText?: string;
}

function getVerdictStyle(classification: string) {
  switch (classification) {
    case 'Qualified': return { bg: 'bg-green-50 border-green-400', text: 'text-green-800', icon: CheckCircle2, iconColor: 'text-green-600' };
    case 'Stretch': return { bg: 'bg-amber-50 border-amber-400', text: 'text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-600' };
    default: return { bg: 'bg-red-50 border-red-400', text: 'text-red-800', icon: XCircle, iconColor: 'text-red-600' };
  }
}

function getScoreColor(score: number) {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

function getSeverityColor(severity: string) {
  if (severity.includes('Disqualifying')) return 'bg-red-100 text-red-800 border-red-300';
  if (severity.includes('Significant')) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-yellow-100 text-yellow-800 border-yellow-300';
}

export function QualificationMatchResults({ result, reportVersion, savedJobId, jobTitle, companyName, jobDescriptionText }: QualificationMatchResultsProps) {
  const navigate = useNavigate();
  const verdict = result.verdict;
  const verdictStyle = getVerdictStyle(verdict?.classification);
  const VerdictIcon = verdictStyle.icon;
  const isExpress = reportVersion === 'express';

  return (
    <div className="space-y-6">
      {/* Header row with status dropdown */}
      {savedJobId && (
        <div className="flex justify-end">
          <ApplicationStatusDropdown savedJobId={savedJobId} currentStatus="saved" />
        </div>
      )}

      {/* Verdict Banner */}
      <Card className={cn("border-2", verdictStyle.bg)}>
        <CardContent className="py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-white/80 shadow-sm">
              <VerdictIcon className={cn("w-8 h-8", verdictStyle.iconColor)} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className={cn("text-2xl font-bold", verdictStyle.text)}>{verdict?.classification}</h2>
                <Badge variant="outline" className="text-xs">{verdict?.confidence} Confidence</Badge>
              </div>
              <p className="text-muted-foreground mt-1">{verdict?.reasoning}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Score Bars (Snapshot+ only) */}
      {!isExpress && result.match_percentage && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Match Scores</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: 'Overall', score: result.match_percentage.overall_score },
              { label: 'Requirements', score: result.match_percentage.requirements_score },
              { label: 'Skills', score: result.match_percentage.skills_score },
              { label: 'Experience', score: result.match_percentage.experience_score },
            ].map(({ label, score }) => (
              <div key={label} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{label}</span>
                  <span className={cn("font-bold", score >= 80 ? 'text-green-700' : score >= 60 ? 'text-amber-700' : 'text-red-700')}>
                    {score}%
                  </span>
                </div>
                <div className="h-3 rounded-full bg-muted overflow-hidden">
                  <div className={cn("h-full rounded-full transition-all", getScoreColor(score))} style={{ width: `${score}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Top Strengths */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" /> Top Strengths
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          {result.top_strengths?.map((s: any, i: number) => (
            <Card key={i} className="border-green-200 bg-green-50/50">
              <CardContent className="pt-4 space-y-2">
                <h4 className="font-semibold text-green-800">{s.title}</h4>
                <p className="text-sm text-muted-foreground">{s.description}</p>
                <p className="text-xs bg-green-100/80 rounded p-2 text-green-700 italic">{s.evidence}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Top Gaps */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" /> Top Gaps
        </h3>
        <div className="grid gap-4 md:grid-cols-3">
          {result.top_gaps?.map((g: any, i: number) => (
            <Card key={i} className="border-amber-200">
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-semibold">{g.title}</h4>
                  <Badge className={cn("text-xs border", getSeverityColor(g.severity))}>{g.severity}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{g.description}</p>
                <Badge variant="outline" className="text-xs">{g.gap_type}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Gap Severity Summary (Snapshot+) */}
      {!isExpress && result.gap_severity_rating && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="font-semibold">{result.gap_severity_rating.overall_severity}</span>
              <div className="flex gap-3">
                <Badge variant="destructive" className="text-xs">{result.gap_severity_rating.disqualifying_gap_count} Disqualifying</Badge>
                <Badge className="text-xs bg-orange-500">{result.gap_severity_rating.significant_gap_count} Significant</Badge>
                <Badge className="text-xs bg-yellow-500 text-yellow-900">{result.gap_severity_rating.minor_gap_count} Minor</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommended Action */}
      <Card className="border-2 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-primary" /> Recommended Action
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <h3 className="text-xl font-bold text-primary">{result.recommended_action?.action}</h3>
          <p className="text-muted-foreground">{result.recommended_action?.action_reasoning}</p>

          {result.recommended_action?.positioning_tips?.length > 0 && (
            <div className="space-y-1 mt-3">
              <p className="text-sm font-medium">Positioning Tips:</p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                {result.recommended_action.positioning_tips.map((tip: string, i: number) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}

          {result.recommended_action?.gap_closing_plan?.length > 0 && (
            <div className="space-y-1 mt-3">
              <p className="text-sm font-medium">Gap Closing Plan:</p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                {result.recommended_action.gap_closing_plan.map((item: any, i: number) => (
                  <li key={i}>{item.step} — <span className="italic">{item.estimated_time}</span></li>
                ))}
              </ul>
            </div>
          )}

          {result.recommended_action?.alternative_roles?.length > 0 && (
            <div className="space-y-1 mt-3">
              <p className="text-sm font-medium">Alternative Roles to Consider:</p>
              <div className="flex flex-wrap gap-2">
                {result.recommended_action.alternative_roles.map((role: string, i: number) => (
                  <Badge key={i} variant="secondary">{role}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
          onClick={() => navigate('/resume-tailoring', {
            state: { jobTitle, companyName, jobDescription: jobDescriptionText }
          })}
        >
          <Target className="w-4 h-4 mr-2" /> Tailor My Resume for This Job
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => navigate('/jobintel-360-reports', {
            state: { triggerReport: 'job_insider', jobTitle, companyName }
          })}
        >
          <Search className="w-4 h-4 mr-2" /> Run Full Job Intelligence Report
        </Button>
      </div>
    </div>
  );
}
